import { Component } from '@theme/component'
import {
  CartUpdateEvent,
  DiscountUpdateEvent,
  ThemeEvents
} from '@theme/events'
import { cartPerformance } from '@theme/performance'
import { morphSection, sectionRenderer } from '@theme/section-renderer'
import { resetShimmer } from '@theme/text-component'
import { debounce, fetchConfig, onAnimationEnd, useReduced } from '@theme/utils'

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this)

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate)
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate
    )
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate)
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    )
    document.removeEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate
    )
  }

  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail

    if (!line) return

    if (quantity === 0) {
      return this.onLineItemRemove(line)
    }

    this.updateQuantity({
      action: 'change',
      line,
      quantity
    })
    const lineItemRow = this.refs.cartItemRows[line - 1]

    if (!lineItemRow) return

    const textComponent = lineItemRow.querySelector('text-component')
    textComponent?.shimmer()
  }

  onLineItemRemove(line) {
    this.updateQuantity({
      action: 'clear',
      line,
      quantity: 0
    })

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1]

    if (!cartItemRowToRemove) return

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      )
    ]

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove()

      if (useReduced()) {
        remove()
        return
      }

      row.style.setProperty('--row-height', `${row.clientHeight}px`)
      row.classList.add('removing')

      onAnimationEnd(row, remove)
    })
  }

  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    )

    this.#disableCartItems()

    const { line, quantity } = config
    const changedLineKey = this.refs.cartItemRows?.[line - 1]?.dataset.key ?? ''
    const { cartTotal } = this.refs

    const cartItemsComponents = document.querySelectorAll('cart-items')
    const sectionsToUpdate = new Set([this.sectionId])
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId)
      }
    })

    const extraSelectors = [
      'cart-rewards-progress',
      'cart-discount',
      'cart-summary'
    ]
    document.querySelectorAll(extraSelectors.join(',')).forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionsToUpdate.add(el.dataset.sectionId)
      }
    })

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname
    })

    cartTotal?.shimmer()

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text()
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText)

        resetShimmer(this)

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText)
          return
        }

        let newCartItemCount = parsedResponseText.item_count
        if (typeof newCartItemCount !== 'number') {
          const match = parsedResponseText.sections[this.sectionId]?.match(
            /ref="cartItemCount"[^>]*>\s*(\d+)\s*</
          )
          newCartItemCount = match ? Number.parseInt(match[1], 10) : 0
        }

        this.dispatchEvent(
          new CartUpdateEvent(
            parsedResponseText,
            `cart-items-${this.sectionId}`,
            {
              changedLineKey,
              changedQuantity: quantity,
              changeType: config.action === 'clear' ? 'remove' : 'update',
              itemCount: newCartItemCount,
              morphed: true,
              sections: parsedResponseText.sections,
              source: 'cart-items'
            }
          )
        )

        for (const id in parsedResponseText.sections) {
          morphSection(id, parsedResponseText.sections[id])
        }
      })
      .catch(() => {
        /* ignore cart refresh error */
      })
      .finally(() => {
        this.#enableCartItems()
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker)
      })
  }

  handleDiscountUpdate = (event) => {
    const { sourceId } = event.detail
    if (typeof sourceId === 'string' && sourceId === this.sectionId) {
      return
    }

    this.#handleCartUpdate(event)
  }

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1]
    const quantityInput = quantitySelector?.querySelector('input')

    if (!quantityInput) throw new Error('Quantity input not found')

    quantityInput.value = quantityInput.defaultValue

    const cartItemError = this.refs[`cartItemError-${line}`]
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`]

    if (!(cartItemError instanceof HTMLElement))
      throw new Error('Cart item error not found')
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error('Cart item error container not found')

    cartItemError.textContent =
      parsedResponseText.errors ||
      parsedResponseText.description ||
      parsedResponseText.message ||
      'Error updating cart'
    cartItemErrorContainer.classList.remove('hidden')
  }

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      if (event.detail?.morphed) return
      sectionRenderer.renderSection(this.sectionId, { cache: false })
      return
    }
    if (event.target === this) return
    if (event.detail?.morphed || event.detail?.data?.morphed) return

    const cartItemsHtml = event.detail.data?.sections?.[this.sectionId]
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml)
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false })
    }
  }

  #disableCartItems() {
    this.classList.add('cart-items-disabled')
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled')
  }

  get sectionId() {
    const { sectionId } = this.dataset

    if (!sectionId) throw new Error('Section id missing')

    return sectionId
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItemsComponent)
}
